import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Helpers } from "src/helpers/helpers";
import { BaseService } from "src/services/base.service";
import { environment } from "src/environments/environment";
import { Observable } from "rxjs";
import { catchError } from "rxjs/operators";
import { Agency, AgencyUser, Batch } from "src/models/agency";
import { NewCustomer } from "src/models/newcustomer";
import { Dashboard } from "src/models/dashboard";
import { DeleteData } from "./delete-data";

@Injectable({
  providedIn: "root",
})
export class AgencyService extends BaseService {
  constructor(private http: HttpClient, helper: Helpers) {
    super(helper);
  }

  private pathAPI = environment.apiUrl;

  getAgencies(): Observable<Agency[]> {
    return this.http
      .get<Agency[]>(this.pathAPI + "agency", this.agencyheader())
      .pipe(catchError(super.handleError));
  }

  createAgency(data: Agency): Observable<void> {
    return this.http
      .post<void>(this.pathAPI + "agency", data, this.agencyheader())
      .pipe(catchError(super.handleError));
  }

  getAgencyById(id: string): Observable<Agency> {
    return this.http
      .get<Agency>(this.pathAPI + "agency/" + id, this.agencyheader())
      .pipe(catchError(super.handleError));
  }

  getAgency(): Observable<Agency> {
    return this.http
      .get<Agency>(this.pathAPI + "agency/mine", this.agencyheader())
      .pipe(catchError(super.handleError));
  }

  createAgencyUser(data: AgencyUser, agencyId: string): Observable<void> {
    return this.http
      .post<void>(
        this.pathAPI + "agency/" + agencyId + "/user",
        data,
        this.agencyheader()
      )
      .pipe(catchError(super.handleError));
  }

  toggleAgencyUserStatus(id: string, activate: boolean): Observable<void> {
    return this.http
      .put<void>(
        this.pathAPI + "agency/user/" + id + "/activate?isActive=" + activate,
        {},
        this.agencyheader()
      )
      .pipe(catchError(super.handleError));
  }

  toggleAgencyStatus(id: string, activate: boolean): Observable<void> {
    return this.http
      .put<void>(
        this.pathAPI + "agency/" + id + "/activate?isActive=" + activate,
        {},
        this.agencyheader()
      )
      .pipe(catchError(super.handleError));
  }

  resetAgencyUserPassword(email: string): Observable<void> {
    const emailEncoded = encodeURIComponent(email);
    return this.http
      .post<void>(
        this.pathAPI + "agency/user/reset-password?email=" + emailEncoded,
        {},
        this.agencyheader()
      )
      .pipe(catchError(super.handleError));
  }

  createBatch(data: Batch): Observable<Batch> {
    return this.http
      .post<Batch>(this.pathAPI + "agency/batch", data, this.agencyheader())
      .pipe(catchError(super.handleError));
  }

  triggerBatch(
    id: number,
    type: "whatsapp" | "email" | "renew"
  ): Observable<void> {
    return this.http
      .post<void>(
        this.pathAPI + "agency/batch/" + id + "/trigger?type=" + type,
        {},
        this.agencyheader()
      )
      .pipe(catchError(super.handleError));
  }

  getBatches(
    status: "PENDING" | "PROCESSING" | "COMPLETED",
    type: "whatsapp" | "email" | "renew"
  ): Observable<Batch[]> {
    return this.http
      .get<Batch[]>(
        this.pathAPI + "agency/batch?status=" + status + "&type=" + type,
        super.header()
      )
      .pipe(catchError(super.handleError));
  }

  changePassword(data: {
    currentPassword: string;
    newPassword: string;
  }): Observable<void> {
    return this.http
      .post<void>(
        this.pathAPI + "agency/user/change-password",
        data,
        this.agencyheader()
      )
      .pipe(catchError(super.handleError));
  }

  triggerCommunication(
    batchId: number,
    type: "whatsapp" | "email" | "renew",
    phoneNumbers?: NewCustomer[],
    emails?: NewCustomer[],
    renew?: NewCustomer[],
    renewType?: "7d" | "15d" | "30d" | "1d" | "2df" | "5d" | "10d" | "20d"
  ): Observable<void> {
    return this.http
      .post<void>(
        this.pathAPI + "agency/batch/" + batchId + "/trigger-communication",
        { type, phoneNumbers, emails, renew, renewType },
        this.agencyheader()
      )
      .pipe(catchError(super.handleError));
  }

  getDashboardCounts(agencyId?: string): Observable<Dashboard> {
    return this.http
      .get<Dashboard>(
        this.pathAPI +
          "agency/dashboard" +
          (agencyId ? "?agencyId=" + agencyId : ""),
        this.agencyheader()
      )
      .pipe(catchError(super.handleError));
  }

  deleteData(data: DeleteData[]): Observable<void> {
    return this.http
      .post<void>(
        this.pathAPI + "agency/delete-data",
        data,
        this.agencyheader()
      )
      .pipe(catchError(super.handleError));
  }

  protected agencyheader() {
    return { headers: super.header().headers.append("accept-language", "en") };
  }
}
